import * as AlertDialog from "@radix-ui/react-alert-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "../../api/http";
import type { ChatAction } from "../../lib/uiStore";
import { Spinner } from "../../ui/Spinner";
import { toast } from "../../ui/toast";
import { tagDot } from "../../lib/tags";
import { CloseIcon } from "../../ui/icons";
import { useChats, useDeleteChat, useRenameChat, useUpdateChat } from "./queries";

// Как CHAT_TAGS в packages/shared; значение не импортируем, чтобы zod не попал в бандл.
const MAX_TAGS = 6;
const MAX_TAG_LEN = 24;

export type { ChatAction } from "../../lib/uiStore";

export const overlay = "fixed inset-0 z-[60] bg-scrim data-[state=open]:animate-[fade_200ms_ease-out]";
export const panel =
  "fixed top-1/2 left-1/2 z-[61] w-[calc(100%-32px)] max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-dialog bg-surface p-6 shadow-floating outline-none data-[state=open]:animate-[dialog_220ms_var(--ease-out)]";
export const btn = "pressable inline-flex h-11 items-center justify-center gap-2 rounded-control px-4 text-sm font-semibold";

export function ChatDialogs({ action, onClose, onDeleted }: { action: ChatAction | null; onClose: () => void; onDeleted: (id: string) => void }) {
  const rename = useRenameChat();
  const del = useDeleteChat();
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (action?.type === "rename") setTitle(action.chat.title ?? "");
  }, [action]);

  const submitRename = (e: FormEvent) => {
    e.preventDefault();
    if (!action || !title.trim()) return;
    rename.mutate(
      { id: action.chat.id, title: title.trim().slice(0, 200) },
      {
        onSuccess: onClose,
        onError: () => toast("Не удалось переименовать чат. Попробуйте ещё раз."),
      },
    );
  };

  const confirmDelete = () => {
    if (!action) return;
    const id = action.chat.id;
    del.mutate(id, {
      onSuccess: () => {
        onClose();
        onDeleted(id);
      },
      onError: (err) =>
        toast(
          err instanceof ApiError && err.code === "run_active"
            ? "Ассистент ещё отвечает в этом чате. Остановите ответ и удалите снова."
            : "Не удалось удалить чат. Попробуйте ещё раз.",
        ),
    });
  };

  return (
    <>
      <Dialog.Root open={action?.type === "rename"} onOpenChange={(o) => !o && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className={overlay} />
          <Dialog.Content className={panel} aria-describedby={undefined}>
            <Dialog.Title className="text-lg font-bold">Переименовать чат</Dialog.Title>
            <form onSubmit={submitRename} className="mt-4">
              <label htmlFor="rename-input" className="sr-only">
                Название чата
              </label>
              <input
                id="rename-input"
                autoFocus
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-12 w-full rounded-control border-[1.5px] border-line px-4 text-base focus:border-accent focus:shadow-[0_0_0_4px_rgb(23_128_60/16%)] focus:outline-none focus-visible:outline-none"
              />
              <div className="mt-5 flex justify-end gap-2">
                <Dialog.Close className={`${btn} text-ink-2 hover:bg-bg`}>Отмена</Dialog.Close>
                <button type="submit" aria-disabled={!title.trim() || rename.isPending} className={`${btn} bg-accent-grad hover-accent-grad text-white`}>
                  {rename.isPending && <Spinner />}
                  Сохранить
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <TagsDialog action={action?.type === "tags" ? action : null} onClose={onClose} />

      <AlertDialog.Root open={action?.type === "delete"} onOpenChange={(o) => !o && onClose()}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className={overlay} />
          <AlertDialog.Content className={panel}>
            <AlertDialog.Title className="text-lg font-bold">Удалить чат?</AlertDialog.Title>
            <AlertDialog.Description className="mt-2 text-[15px] leading-[22px] text-sub">
              «{action?.chat.title ?? "Новый чат"}» и вся переписка в нём будут удалены без возможности восстановления.
            </AlertDialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <AlertDialog.Cancel className={`${btn} text-ink-2 hover:bg-bg`}>Отмена</AlertDialog.Cancel>
              <button type="button" onClick={confirmDelete} aria-disabled={del.isPending} className={`${btn} bg-danger text-white hover:bg-danger-hover`}>
                {del.isPending && <Spinner />}
                Удалить
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}

/** Метки чата: добавить из подсказок или новую, убрать крестиком. Сохраняется кнопкой. */
function TagsDialog({ action, onClose }: { action: ChatAction | null; onClose: () => void }) {
  const update = useUpdateChat();
  const chats = useChats();
  const [tags, setTags] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (action) {
      setTags(action.chat.tags ?? []);
      setDraft("");
    }
  }, [action]);

  const has = (t: string) => tags.some((x) => x.toLowerCase() === t.toLowerCase());
  const known = [...new Set((chats.data ?? []).flatMap((c) => c.tags ?? []))].filter((t) => !has(t)).slice(0, 12);
  const add = (raw: string) => {
    const t = raw.replace(/[,\n\r\t]+/g, " ").trim().slice(0, MAX_TAG_LEN);
    if (!t || has(t)) return setDraft("");
    if (tags.length >= MAX_TAGS) return toast(`У чата может быть не больше ${MAX_TAGS} меток`);
    setTags([...tags, t]);
    setDraft("");
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!action) return;
    const next = draft.trim() && !has(draft.trim()) && tags.length < MAX_TAGS ? [...tags, draft.trim().slice(0, MAX_TAG_LEN)] : tags;
    update.mutate(
      { id: action.chat.id, patch: { tags: next } },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open={!!action} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlay} />
        <Dialog.Content className={panel}>
          <Dialog.Title className="text-lg font-bold">Метки чата</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] leading-[18px] text-sub">
            По меткам удобно фильтровать историю: «Договоры», «Совещания», «Q4».
          </Dialog.Description>
          <form onSubmit={save} className="mt-4">
            <div className="flex min-h-12 flex-wrap items-center gap-1.5 rounded-control border-[1.5px] border-line px-2 py-1.5 focus-within:border-accent focus-within:shadow-[0_0_0_4px_rgb(23_128_60/16%)]">
              {tags.map((t) => (
                <span key={t} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-muted pr-1 pl-2.5 text-[13px] font-medium">
                  <span aria-hidden className={`size-2 rounded-full ${tagDot(t)}`} />
                  {t}
                  <button
                    type="button"
                    aria-label={`Убрать метку «${t}»`}
                    onClick={() => setTags(tags.filter((x) => x !== t))}
                    className="grid size-6 place-items-center rounded-full text-sub hover:bg-hover hover:text-ink"
                  >
                    <CloseIcon className="size-3.5" />
                  </button>
                </span>
              ))}
              <label htmlFor="tag-input" className="sr-only">
                Новая метка
              </label>
              <input
                id="tag-input"
                autoFocus
                value={draft}
                maxLength={MAX_TAG_LEN}
                placeholder={tags.length ? "Ещё метка" : "Новая метка"}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === ",") && draft.trim()) {
                    e.preventDefault();
                    add(draft);
                  } else if (e.key === "Backspace" && !draft && tags.length) {
                    setTags(tags.slice(0, -1));
                  }
                }}
                className="h-8 min-w-24 flex-1 bg-transparent px-1 text-base outline-none placeholder:text-placeholder focus-visible:outline-none"
              />
            </div>
            {known.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Метки других чатов" role="group">
                {known.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => add(t)}
                    className="pressable relative inline-flex h-8 items-center gap-1.5 rounded-full border border-line px-2.5 text-[13px] text-ink-2 after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-[''] hover:bg-hover"
                  >
                    <span aria-hidden>+</span>
                    <span aria-hidden className={`size-2 rounded-full ${tagDot(t)}`} />
                    {t}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Dialog.Close className={`${btn} text-ink-2 hover:bg-bg`}>Отмена</Dialog.Close>
              <button type="submit" aria-disabled={update.isPending} className={`${btn} bg-accent-grad hover-accent-grad text-white`}>
                {update.isPending && <Spinner />}
                Сохранить
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
